/**
 * Word Factory Autofill
 * Auto-fills Teeinblue personalization fields with Word Factory data.
 *
 * Mug page keeps its original locked-flow behavior (unchanged, in case the
 * mug returns). Poster and card pages get a light-touch fill: populate the
 * Teeinblue fields once, never redirect, never lock the page, and never
 * overwrite a customer's manual edits.
 */

document.addEventListener("DOMContentLoaded", () => {
  const path = window.location.pathname;
  const isMugProductPage = path.includes("/products/personalized-mug");
  const isPosterProductPage = path.includes("/products/enhanced-matte-paper-framed-poster");
  const isCardProductPage = path.includes("/products/greeting-card");

  if (isMugProductPage) {
    runMugAutofill();
    return;
  }

  if (isPosterProductPage || isCardProductPage) {
    runGeneralAutofill();
  }
});

function injectProductPageCleanup() {
  // Hide the theme's variant-picker bars (Available Product / Color / Size)
  // and Teeinblue's own size/option rows. Single-variant products need none
  // of them, and the size choice is forced to 5"x7" below.
  const style = document.createElement("style");
  style.textContent = `
    variant-picker,
    .variant-picker,
    .tee-row.tee-option-inner,
    .tee-option--product,
    .tee-option--color,
    .tee-option--size {
      display: none !important;
    }
  `;
  document.head.appendChild(style);

  const isPosterProductPage = window.location.pathname.includes("/products/enhanced-matte-paper-framed-poster");
  const sizePattern = isPosterProductPage
    ? /18\s*["\u2033\u201D]?\s*[x\u00D7]\s*24\s*["\u2033\u201D]?/i
    : /5\s*["\u2033\u201D]?\s*[x\u00D7]\s*7\s*["\u2033\u201D]?/i;

  const selectTargetSize = () => {
    const sizeRadios = [
      ...document.querySelectorAll('input[type="radio"][name^="size-"], input[type="radio"][name*="Size"]')
    ];
    let foundTargetSize = false;

    for (const radio of sizeRadios) {
      const label = radio.id ? document.querySelector(`label[for="${radio.id}"]`) : null;
      const text = `${radio.value || ""} ${label?.textContent || ""}`.trim();
      if (!sizePattern.test(text)) continue;
      foundTargetSize = true;
      if (radio.checked) continue;

      if (label) label.click();
      radio.checked = true;
      radio.dispatchEvent(new Event("input", { bubbles: true }));
      radio.dispatchEvent(new Event("change", { bubbles: true }));
    }
    if (foundTargetSize) return true;

    const candidates = document.querySelectorAll(".tee-option-item, .tee-option button, .tee-option label, button, label");
    for (const el of candidates) {
      const text = (el.textContent || "").trim();
      if (text.length > 12 || !sizePattern.test(text)) continue;
      el.click();
      return true;
    }
    return false;
  };

  const blankEmptyPronunciation = () => {
    const containers = document.querySelectorAll(".tee-field, .tee-row, [class*='tee-field'], .nfw-pronunciation");
    for (const container of containers) {
      const containerText = container.textContent || "";
      if (containerText.length > 80 || !/pronunciation/i.test(containerText)) continue;
      const field = container.querySelector("textarea, input[type='text'], input:not([type])");
      if (field && !field.value.trim() && !field.dataset.nwfBlanked) {
        field.dataset.nwfBlanked = "1";
        field.value = "\u200B";
        ["input", "change", "keyup", "blur"].forEach(type => {
          field.dispatchEvent(new Event(type, { bubbles: true }));
        });
      }
      return;
    }
  };

  const selectDefaultFontOptions = () => {
    const fontFields = document.querySelectorAll('.tee-field[class*="-font"]');

    fontFields.forEach(field => {
      const radios = [...field.querySelectorAll('input[type="radio"]')];
      if (!radios.length || radios.some(radio => radio.checked)) return;

      let choice = radios[0];
      if (/layer-385979-font|layer-836073-font/.test(field.id)) {
        choice = radios[1] || radios[0];
      }

      const label = choice.id ? field.querySelector(`label[for="${choice.id}"]`) : null;
      if (label) label.click();
      else choice.click();
      choice.checked = true;
      choice.dispatchEvent(new Event("input", { bubbles: true }));
      choice.dispatchEvent(new Event("change", { bubbles: true }));
    });
  };

  let ticks = 0;
  const cleanupInterval = setInterval(() => {
    ticks += 1;
    selectTargetSize();
    blankEmptyPronunciation();
    selectDefaultFontOptions();
    if (ticks >= 80) clearInterval(cleanupInterval);
  }, 250);
}

function runGeneralAutofill() {
  injectProductPageCleanup();

  const storageGet = key => {
    try {
      return localStorage.getItem(key) || "";
    } catch (err) {
      return "";
    }
  };

  const clean = value => String(value || "").replace(/\s+/g, " ").trim();

  const formatDefinition = value => {
    const text = clean(value)
      .replace(/^\*?adjective\*?\.\s*/i, "")
      .replace(/^adj\.\s*/i, "")
      .replace(/^\*?verb\*?\.\s*/i, "")
      .replace(/^v\.\s*/i, "");

    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const word = clean(storageGet("nwf_word"));
  const definition = formatDefinition(storageGet("nwf_definition") || storageGet("nwf_description"));
  const sampleSentence = clean(storageGet("nwf_sample_sentence") || storageGet("nwf_example"));

  if (!word && !definition && !sampleSentence) {
    console.log("No Word Factory data found; leaving the product page as is.");
    return;
  }

  console.log("Word Factory data found, waiting for Teeinblue form.");

  const setFieldValue = (field, value) => {
    if (!field || value === null || value === undefined) return;
    const nextValue = String(value);
    if (field.value !== nextValue) field.value = nextValue;
    field.dispatchEvent(new Event("input", { bubbles: true }));
    field.dispatchEvent(new Event("change", { bubbles: true }));
    field.dispatchEvent(new KeyboardEvent("keyup", { bubbles: true }));
    field.dispatchEvent(new Event("blur", { bubbles: true }));
  };

  const findField = (selectors, labelPattern) => {
    for (const selector of selectors) {
      const field = document.querySelector(selector);
      if (field) return field;
    }

    const containers = [
      ...document.querySelectorAll(".tee-field, .tee-row, .tee-option, [class*='tee-field']")
    ];

    for (const container of containers) {
      const text = (container.textContent || "").toLowerCase();
      if (!labelPattern.test(text)) continue;

      const field = container.querySelector("textarea, input[type='text'], input:not([type])");
      if (field) return field;
    }

    return null;
  };

  // Watch for real (trusted) user edits so we never overwrite a customer's
  // own typing. Programmatic writes (Teeinblue restoring stale session text)
  // do not set isTrusted, so those still get re-asserted below.
  const watchUserEdits = field => {
    if (!field || field.dataset.nwfEditWatch) return;
    field.dataset.nwfEditWatch = "1";
    field.addEventListener("input", event => {
      if (event.isTrusted) field.dataset.nwfUserEdited = "1";
    });
  };

  const assertField = (field, value) => {
    if (!field || !value) return false;
    watchUserEdits(field);
    if (field.dataset.nwfUserEdited) return true;
    if (document.activeElement === field) return true;
    if (field.value !== value) setFieldValue(field, value);
    return true;
  };

  const applyPersonalization = () => {
    const wordField = findField(
      [".nfw-title .tee-field__input input", ".nfw-title input", ".nwf-word input"],
      /word/
    );
    const definitionField = findField(
      [".nfw-definition .tee-field__input textarea", ".nfw-definition textarea", ".nfw-description textarea"],
      /definition/
    );
    const exampleField = findField(
      [".nfw-sample-sentence .tee-field__input textarea", ".nfw-sample-sentence .tee-field__input input"],
      /example|sample/
    );
    const pronunciationField = findField(
      [".nfw-pronunciation .tee-field__input input", ".nfw-pronunciation input"],
      /pronunciation/
    );

    assertField(wordField, word);
    assertField(definitionField, definition);
    assertField(exampleField, sampleSentence);
    assertField(pronunciationField, clean(storageGet("nwf_pronunciation")));

    return Boolean(wordField || definitionField || exampleField);
  };

  let checks = 0;
  let hasLoggedFound = false;
  const teeInterval = setInterval(() => {
    checks += 1;
    const found = applyPersonalization();

    if (found && !hasLoggedFound) {
      console.log("Teeinblue form found and personalized.");
      hasLoggedFound = true;
    }

    if (checks >= 80) {
      clearInterval(teeInterval);
      console.log("Stopped checking for Teeinblue form.");
    }
  }, 250);

  document.addEventListener("click", event => {
    if (event.target.closest("button, [role='button'], a")) applyPersonalization();
  }, true);

  document.addEventListener("submit", () => {
    applyPersonalization();
  }, true);
}

function runMugAutofill() {
  const NWF_SIMPLE_MUG_DESCRIPTION = "15 oz white glossy ceramic mug.";
  const NWF_WORD_LIVE_AREA_WIDTH = 285;

  const storageGet = key => {
    try {
      return localStorage.getItem(key) || "";
    } catch (err) {
      return "";
    }
  };

  const storageRemove = key => {
    try {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    } catch (err) {
      console.warn("Unable to clear retired Nerd Word data.", err);
    }
  };

  storageRemove("nwf_pronunciation");
  storageRemove("nwf_origin");

  const normalizeMugText = value =>
    String(value || "")
      .replace(/\s+/g, " ")
      .trim();

  const formatDefinition = value => {
    const text = normalizeMugText(value)
      .replace(/^\*?adjective\*?\.\s*/i, "")
      .replace(/^adj\.\s*/i, "")
      .replace(/^\*?verb\*?\.\s*/i, "")
      .replace(/^v\.\s*/i, "");

    return text ? `Adjective. ${text}` : "";
  };

  const word = normalizeMugText(storageGet("nwf_word"));
  const definition = formatDefinition(storageGet("nwf_definition") || storageGet("nwf_description"));
  const sampleSentence = normalizeMugText(storageGet("nwf_sample_sentence") || storageGet("nwf_example"));

  if (!word && !definition && !sampleSentence) {
    console.log("No Word Factory data found.");
    window.location.replace("/?nwf=make-word");
    return;
  }

  console.log("Word Factory data found, waiting for Teeinblue form.");
  document.body.classList.add("nwf-locked-product");
  localStorage.setItem("nwf_checkout_after_add", "1");

  const style = document.createElement("style");
  style.textContent = `
    body.nwf-locked-product .tee-customize-main-form,
    body.nwf-locked-product .tee-form-header,
    body.nwf-locked-product .tee-form-action:not(.tee-form-atc-wrapper),
    body.nwf-locked-product .tee-row.tee-option-inner,
    body.nwf-locked-product .tee-option--color,
    body.nwf-locked-product .tee-option--size,
    body.nwf-locked-product .tee-btn--preview,
    body.nwf-locked-product variant-picker,
    body.nwf-locked-product .variant-picker,
    body.nwf-locked-product add-to-cart-component,
    body.nwf-locked-product .add-to-cart-button,
    body.nwf-locked-product .shopify-payment-button,
    body.nwf-locked-product .nfw-pronunciation,
    body.nwf-locked-product .nfw-origin,
    body.nwf-locked-product [data-testid="product-recommendations-section"] {
      display: none !important;
    }
  `;
  document.head.appendChild(style);

  const hideOtherProducts = () => {
    document.querySelectorAll('a[href*="greeting-card"], a[href*="enhanced-matte-paper-framed-poster"]').forEach(link => {
      const card = link.closest(".product-card, li, .grid__item, .shopify-section") || link;
      card.style.display = "none";
    });
  };

  const tunePurchaseControls = () => {
    document.querySelectorAll("#teeAtcButton, button").forEach(button => {
      const label = (button.textContent || "").trim().toLowerCase();
      if (label === "add to cart") button.textContent = "CHECK OUT";
    });
  };

  const normalizeMugDescription = () => {
    document.querySelectorAll(".tee-description__content").forEach(content => {
      content.innerHTML = `<p>${NWF_SIMPLE_MUG_DESCRIPTION}</p>`;
      content.style.height = "auto";
    });

    document.querySelectorAll("rte-formatter, .text-block").forEach(block => {
      if (block.closest(".tee-description")) return;

      const text = (block.textContent || "").replace(/\s+/g, " ").trim();
      if (!/15\s*oz white glossy ceramic mug|15oz white glossy ceramic mug|Height:\s*4\.7|Print area|Microwave and dishwasher/i.test(text)) return;

      block.remove();
    });
  };

  const scoreLineBreaks = (lines, maxChars) => {
    const lengths = lines.map(line => line.length);
    const target = lengths.reduce((sum, length) => sum + length, 0) / lines.length;
    const longest = Math.max(...lengths);
    const shortest = Math.min(...lengths);

    return lengths.reduce((score, length, index) => {
      const words = lines[index].split(" ").filter(Boolean).length;
      const overflow = Math.max(0, length - maxChars);
      const widow = words <= 1 || length < target * 0.45;
      return score +
        Math.pow(length - target, 2) +
        Math.pow(overflow, 2) * 4 +
        (widow ? 500 : 0);
    }, Math.pow(longest - shortest, 2));
  };

  const findBalancedLines = (words, lineCount, maxChars) => {
    const minimumWords = words.length >= lineCount * 2 ? 2 : 1;
    let best = null;

    const walk = (start, lines) => {
      const remainingLines = lineCount - lines.length;
      const remainingWords = words.length - start;

      if (remainingLines === 1) {
        if (remainingWords < minimumWords) return;
        const candidate = [...lines, words.slice(start).join(" ")];
        const score = scoreLineBreaks(candidate, maxChars);
        if (!best || score < best.score) best = { lines: candidate, score };
        return;
      }

      const minEnd = start + minimumWords;
      const maxEnd = words.length - minimumWords * (remainingLines - 1);
      for (let end = minEnd; end <= maxEnd; end += 1) {
        walk(end, [...lines, words.slice(start, end).join(" ")]);
      }
    };

    walk(0, []);
    return best?.lines || [words.join(" ")];
  };

  const balanceMugLines = (value, settings = {}) => {
    const text = normalizeMugText(value);
    if (!text) return "";

    const words = text.split(" ");
    if (words.length <= 3) return text;

    const maxChars = settings.maxChars || 30;
    const minLines = settings.minLines || 2;
    const maxLines = settings.maxLines || 3;
    const lineCount = Math.max(
      minLines,
      Math.min(maxLines, Math.ceil(text.length / maxChars))
    );

    if (lineCount <= 1) return text;
    return findBalancedLines(words, lineCount, maxChars).join("\n");
  };

  const balancedDefinition = balanceMugLines(definition, {
    minLines: definition.length > 44 ? 2 : 1,
    maxLines: 3,
    maxChars: 38
  });
  const balancedSampleSentence = balanceMugLines(sampleSentence, {
    minLines: sampleSentence.length > 42 ? 2 : 1,
    maxLines: 3,
    maxChars: 34
  });
  const mugDefinitionText = ["Definition:", balancedDefinition].filter(Boolean).join("\n");
  const mugSampleSentenceText = ["Example:", balancedSampleSentence].filter(Boolean).join("\n");
  const retiredLayerBlank = "\u200B";

  const setFieldValue = (field, value) => {
    if (!field || value === null || value === undefined) return;
    const nextValue = String(value);
    if (field.value !== nextValue) field.value = nextValue;
    field.required = false;
    field.removeAttribute("required");
    field.dispatchEvent(new Event("input", { bubbles: true }));
    field.dispatchEvent(new Event("change", { bubbles: true }));
    field.dispatchEvent(new KeyboardEvent("keyup", { bubbles: true }));
    field.dispatchEvent(new Event("blur", { bubbles: true }));
  };

  const retiredFieldPattern = /pronunciation|origin|forged\s+from/i;
  const legacyRetiredValuePattern = /am-wor-pa|vel-mor-ah|forged\s+from/i;

  const fieldContainerText = field => {
    const container = field.closest(".tee-field, .tee-row, .tee-option, [class*='tee-field'], .nfw-pronunciation, .nfw-origin");
    return [
      field.name,
      field.id,
      field.className,
      field.getAttribute("aria-label"),
      field.getAttribute("placeholder"),
      field.value,
      container?.className,
      container?.textContent
    ].join(" ");
  };

  const isRetiredField = field => {
    const haystack = fieldContainerText(field);
    return retiredFieldPattern.test(haystack) || legacyRetiredValuePattern.test(haystack);
  };

  const clearRetiredFields = () => {
    document.querySelectorAll("input, textarea").forEach(field => {
      if (!isRetiredField(field)) return;
      field.dataset.nwfRetiredField = "true";
      setFieldValue(field, retiredLayerBlank);
    });
  };

  const scrubRetiredArtworkLayers = () => {
    const retiredLayers = new Set([
      ...document.querySelectorAll("#artwork-layer--layer-547556, #artwork-layer--layer-682450")
    ]);

    document.querySelectorAll("svg text, svg tspan").forEach(textNode => {
      if (!retiredFieldPattern.test(textNode.textContent || "") && !legacyRetiredValuePattern.test(textNode.textContent || "")) return;
      const layer = textNode.closest("[id^='artwork-layer--']");
      if (layer) retiredLayers.add(layer);
    });

    retiredLayers.forEach(layer => {
      layer.style.display = "none";
      layer.style.visibility = "hidden";
      layer.style.opacity = "0";
      layer.setAttribute("display", "none");
      layer.setAttribute("opacity", "0");
      layer.setAttribute("aria-hidden", "true");

      layer.querySelectorAll("text, tspan").forEach(textNode => {
        textNode.textContent = "";
      });
    });
  };

  const readTranslate = value => {
    const match = String(value || "").match(/translate\(\s*([-0-9.]+)[,\s]+([-0-9.]+)/);
    if (!match) return null;
    return {
      x: Number(match[1]),
      y: Number(match[2])
    };
  };

  const readScale = value => {
    const match = String(value || "").match(/scale\(\s*([-0-9.]+)(?:[,\s]+([-0-9.]+))?\s*\)/);
    if (!match) return 1;

    const scale = Number(match[1]);
    return Number.isFinite(scale) && scale > 0 ? scale : 1;
  };

  const setGroupTransform = (group, x, y, scale) => {
    if (!group || !Number.isFinite(x) || !Number.isFinite(y)) return;

    let nextTransform = group.getAttribute("transform") || "";
    const translateValue = `translate(${x} ${y})`;
    if (/translate\(\s*[-0-9.]+[,\s]+[-0-9.]+\s*\)/.test(nextTransform)) {
      nextTransform = nextTransform.replace(/translate\(\s*[-0-9.]+[,\s]+[-0-9.]+\s*\)/, translateValue);
    } else {
      nextTransform = `${translateValue} ${nextTransform}`;
    }

    if (Number.isFinite(scale) && scale > 0) {
      const scaleValue = `scale(${scale}, ${scale})`;
      if (/scale\(\s*[-0-9.]+(?:[,\s]+[-0-9.]+)?\s*\)/.test(nextTransform)) {
        nextTransform = nextTransform.replace(/scale\(\s*[-0-9.]+(?:[,\s]+[-0-9.]+)?\s*\)/, scaleValue);
      } else {
        nextTransform = `${nextTransform} ${scaleValue}`;
      }
    }

    group.setAttribute("transform", nextTransform.replace(/\s+/g, " ").trim());
    group.setAttribute("transform-origin", `0px ${y}px`);
  };

  const getWordCenterX = () => {
    const wordTextGroup = document.querySelector("#artwork-layer--layer-229069 g[transform*='translate']");
    const wordTranslate = readTranslate(wordTextGroup?.getAttribute("transform"));
    if (Number.isFinite(wordTranslate?.x)) return wordTranslate.x;

    const wordLayer = document.querySelector("#artwork-layer--layer-229069");
    const rotateMatch = String(wordLayer?.getAttribute("transform") || "").match(/rotate\([^,]+,\s*([-0-9.]+)/);
    if (rotateMatch) return Number(rotateMatch[1]);

    const artwork = document.querySelector("#tee-artwork-1041641, .tee-mockup-artwork");
    const viewBox = String(artwork?.getAttribute("viewBox") || "").split(/\s+/).map(Number);
    if (Number.isFinite(viewBox[2])) return viewBox[2] / 2;

    return 146.11111111111111;
  };

  const getLayerTextGroup = selector =>
    document.querySelector(`${selector} g[transform*='translate']`);

  const getLayerY = selector => {
    const translate = readTranslate(getLayerTextGroup(selector)?.getAttribute("transform"));
    return Number.isFinite(translate?.y) ? translate.y : null;
  };

  const getLayerScale = selector =>
    readScale(getLayerTextGroup(selector)?.getAttribute("transform"));

  const getLayerFontSize = selector => {
    const textNode = document.querySelector(`${selector} text`);
    const attrSize = Number.parseFloat(textNode?.getAttribute("font-size"));
    if (Number.isFinite(attrSize)) return attrSize;

    const computedSize = Number.parseFloat(textNode ? getComputedStyle(textNode).fontSize : "");
    return Number.isFinite(computedSize) ? computedSize : null;
  };

  const countVisibleLines = selector => {
    const values = [...document.querySelectorAll(`${selector} tspan`)]
      .map(tspan => (tspan.textContent || "").trim())
      .filter(text => text && text !== "a");

    return values.length || 1;
  };

  const getSmallTextFontSize = () =>
    getLayerFontSize("#artwork-layer--layer-824613") ||
    getLayerFontSize("#artwork-layer--layer-361097") ||
    7.2081481481481475;

  const getTextLayoutY = () => {
    const wordY = getLayerY("#artwork-layer--layer-229069") || 8.279629629629628;
    const wordFontSize = getLayerFontSize("#artwork-layer--layer-229069") || 19;
    const wordScale = getLayerScale("#artwork-layer--layer-229069") || 1;
    const smallFontSize = getSmallTextFontSize();
    const lineHeight = smallFontSize * 1.26;
    const blockGap = smallFontSize * 0.8;
    const wordToDefinitionGap = blockGap;
    const definitionLines = countVisibleLines("#artwork-layer--layer-361097");
    const definitionY = wordY + (wordFontSize * wordScale * 0.5) + wordToDefinitionGap;
    const exampleY = definitionY + (definitionLines * lineHeight) + blockGap;

    return {
      definitionY,
      exampleY,
      fontSize: smallFontSize
    };
  };

  const centerArtworkLayer = (selector, options = {}) => {
    const layer = document.querySelector(selector);
    if (!layer) return;

    const centerX = getWordCenterX();
    const textGroup = layer.querySelector("g[transform*='translate']");
    const textTranslate = readTranslate(textGroup?.getAttribute("transform"));
    const targetY = Number.isFinite(options.y) ? options.y : textTranslate?.y;

    if (textGroup && Number.isFinite(targetY)) {
      setGroupTransform(textGroup, centerX, targetY);
    }

    layer.querySelectorAll("text").forEach(textNode => {
      textNode.setAttribute("text-anchor", "middle");
      textNode.setAttribute("x", "0");
      if (Number.isFinite(options.fontSize)) textNode.setAttribute("font-size", String(options.fontSize));
      textNode.style.textAnchor = "middle";
      textNode.style.textAlign = "center";
    });

    layer.querySelectorAll("tspan").forEach(tspan => {
      tspan.setAttribute("x", "0");
    });
  };

  const fitWordToLiveArea = () => {
    const layer = document.querySelector("#artwork-layer--layer-229069");
    const textNode = layer?.querySelector("text");
    const textGroup = layer?.querySelector("g[transform*='translate']");
    const textBox = textNode?.getBBox?.();
    const textWidth = textBox?.width;
    if (!textGroup || !Number.isFinite(textWidth) || textWidth <= 0) return;

    const centerX = getWordCenterX();
    const textTranslate = readTranslate(textGroup.getAttribute("transform"));
    const targetY = Number.isFinite(textTranslate?.y) ? textTranslate.y : getLayerY("#artwork-layer--layer-229069");
    const targetScale = Math.min(1, NWF_WORD_LIVE_AREA_WIDTH / textWidth);

    setGroupTransform(textGroup, centerX, targetY, targetScale);
    textNode.setAttribute("text-anchor", "middle");
    textNode.setAttribute("x", "0");
    textNode.style.textAnchor = "middle";
    textNode.style.textAlign = "center";
  };

  let artworkCenteringQueued = false;
  const getLayerScreenRect = selector => {
    const layer = document.querySelector(selector);
    if (!layer || getComputedStyle(layer).display === "none") return null;

    const rects = [...layer.querySelectorAll("text")]
      .filter(textNode => getComputedStyle(textNode).opacity !== "0")
      .map(textNode => textNode.getBoundingClientRect())
      .filter(rect => rect.width > 0 && rect.height > 0);

    if (!rects.length) return null;

    return {
      top: Math.min(...rects.map(rect => rect.top)),
      bottom: Math.max(...rects.map(rect => rect.bottom))
    };
  };

  const screenDeltaToSvgY = delta => {
    const artwork = document.querySelector("#tee-artwork-1041641, .tee-mockup-artwork");
    const rect = artwork?.getBoundingClientRect();
    const viewBox = String(artwork?.getAttribute("viewBox") || "").split(/\s+/).map(Number);
    const viewBoxHeight = viewBox[3];

    if (!rect?.height || !Number.isFinite(viewBoxHeight)) return delta;
    return delta * (viewBoxHeight / rect.height);
  };

  const moveArtworkLayerY = (selector, deltaSvgY) => {
    if (!Number.isFinite(deltaSvgY) || Math.abs(deltaSvgY) < 0.01) return;

    const group = getLayerTextGroup(selector);
    const translate = readTranslate(group?.getAttribute("transform"));
    if (!group || !Number.isFinite(translate?.x) || !Number.isFinite(translate?.y)) return;

    const nextY = translate.y + deltaSvgY;
    const currentTransform = group.getAttribute("transform") || "";
    group.setAttribute(
      "transform",
      currentTransform.replace(/translate\(\s*[-0-9.]+[,\s]+[-0-9.]+\s*\)/, `translate(${translate.x} ${nextY})`)
    );
    group.setAttribute("transform-origin", `0px ${nextY}px`);
  };

  const equalizeVisibleBlockGaps = () => {
    const wordRect = getLayerScreenRect("#artwork-layer--layer-229069");
    const definitionRect = getLayerScreenRect("#artwork-layer--layer-361097");
    const exampleRect = getLayerScreenRect("#artwork-layer--layer-824613");
    if (!wordRect || !definitionRect || !exampleRect) return;

    const gapAfterWord = definitionRect.top - wordRect.bottom;
    const gapAfterDefinition = exampleRect.top - definitionRect.bottom;
    const targetGap = (gapAfterWord + gapAfterDefinition) / 2;

    const definitionDelta = targetGap - gapAfterWord;
    const exampleDelta = (targetGap - gapAfterDefinition) + definitionDelta;

    moveArtworkLayerY("#artwork-layer--layer-361097", screenDeltaToSvgY(definitionDelta));
    moveArtworkLayerY("#artwork-layer--layer-824613", screenDeltaToSvgY(exampleDelta));
  };

  const centerActiveArtworkLayers = () => {
    fitWordToLiveArea();
    const layout = getTextLayoutY();
    centerArtworkLayer("#artwork-layer--layer-361097", {
      y: layout.definitionY,
      fontSize: layout.fontSize
    });
    centerArtworkLayer("#artwork-layer--layer-824613", {
      y: layout.exampleY,
      fontSize: layout.fontSize
    });
    requestAnimationFrame(equalizeVisibleBlockGaps);
  };

  const queueArtworkCentering = () => {
    if (artworkCenteringQueued) return;
    artworkCenteringQueued = true;

    [0, 75, 200, 500, 900].forEach((delay, index, delays) => {
      setTimeout(() => {
        centerActiveArtworkLayers();
        if (index === delays.length - 1) artworkCenteringQueued = false;
      }, delay);
    });
  };

  const closePreviewModal = () => {
    document.querySelectorAll("button, [role='button']").forEach(button => {
      const label = (button.textContent || "").trim().toLowerCase();
      if (label === "close preview") button.click();
    });
  };

  const applyPersonalization = () => {
    const wordInput = document.querySelector(".nfw-title .tee-field__input input, .nfw-title input, .nwf-word input");
    const defTextarea = document.querySelector(".nfw-definition .tee-field__input textarea, .nfw-definition textarea, .nfw-description textarea");
    const sampleInput = document.querySelector(".nfw-sample-sentence .tee-field__input textarea, .nfw-sample-sentence .tee-field__input input, .nwf-sample textarea, .nwf-sample input");
    const foundAnyField = Boolean(wordInput || defTextarea || sampleInput || document.querySelector(".nfw-pronunciation, .nfw-origin"));

    hideOtherProducts();
    tunePurchaseControls();
    normalizeMugDescription();
    clearRetiredFields();
    scrubRetiredArtworkLayers();
    centerActiveArtworkLayers();

    if (wordInput && word) setFieldValue(wordInput, word);
    if (sampleInput && mugSampleSentenceText) setFieldValue(sampleInput, mugSampleSentenceText);

    if (defTextarea && (definition || sampleSentence)) {
      const fallbackLines = [mugDefinitionText, mugSampleSentenceText].filter(Boolean).join("\n\n");
      const fulfillmentText = sampleInput
        ? (mugDefinitionText || "")
        : fallbackLines;
      setFieldValue(defTextarea, fulfillmentText);
    }

    queueArtworkCentering();
    return foundAnyField;
  };

  let checks = 0;
  let hasLoggedFound = false;
  const teeInterval = setInterval(() => {
    checks += 1;
    const found = applyPersonalization();

    if (found && !hasLoggedFound) {
      console.log("Teeinblue form found and personalized.");
      hasLoggedFound = true;
      setTimeout(closePreviewModal, 750);
    }

    if (checks >= 80) {
      clearInterval(teeInterval);
      console.log("Stopped checking for Teeinblue form.");
    }
  }, 250);

  document.addEventListener("click", event => {
    if (event.target.closest("button, [role='button'], a")) applyPersonalization();
  }, true);

  document.addEventListener("submit", () => {
    applyPersonalization();
  }, true);
}
